<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { site } from '@/i18n'
import Icon from '@/components/Icon.vue'

const dock = site.dock
const open = ref(false)
const showTop = ref(false)

function onScroll() {
  showTop.value = window.scrollY > 480
}

function toTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <aside class="side-dock">
    <!-- 官方域名 · 竖排艺术字 -->
    <div class="dock-domain" :title="site.ui.officialDomain + ' ' + dock.domain">
      <span
        v-for="(ch, i) in dock.domain.split('')"
        :key="i"
        class="dock-char mono"
        :class="{ dot: ch === '.' }"
        :style="{ animationDelay: `${i * 90}ms` }"
      >{{ ch }}</span>
      <span class="dock-dot-live"/>
    </div>

    <!-- 联系客服 · 展开多联系方式 -->
    <div class="dock-contact">
      <button
        type="button"
        class="dock-btn"
        :class="{ active: open }"
        :aria-label="site.ui.contactService"
        @click="open = !open"
      >
        <Icon name="Headset"/>
        <span class="dock-btn-text">{{ site.ui.service }}</span>
      </button>

      <Transition name="pop">
        <div v-if="open" class="contact-panel" role="menu">
          <div class="contact-head">
            <strong>{{ site.ui.contactUs }}</strong>
            <span>{{ site.ui.responseNote }}</span>
          </div>
          <a
            v-for="c in dock.contacts"
            :key="c.name"
            class="contact-item"
            :href="c.href"
            target="_blank"
            rel="noopener"
          >
            <span class="contact-icon">
              <Icon :name="c.icon"/>
            </span>
            <span class="contact-info">
              <strong>{{ c.name }}</strong>
              <em>{{ c.value }}</em>
            </span>
            <Icon name="ArrowRight"/>
          </a>
        </div>
      </Transition>
    </div>

    <!-- 返回顶部 -->
    <button
      v-show="showTop"
      type="button"
      class="dock-btn top"
      :aria-label="site.ui.backToTop"
      @click="toTop"
    >
      <Icon name="ArrowUp"/>
    </button>
  </aside>
</template>

<style scoped>
.side-dock {
  position: fixed;
  right: 18px;
  bottom: 120px;
  z-index: 90;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  animation: dock-in 0.6s var(--ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1)) both;
}

@keyframes dock-in {
  from { opacity: 0; transform: translateX(24px); }
  to { opacity: 1; transform: translateX(0); }
}

/* ---- 域名竖排艺术字 ---- */
.dock-domain {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 14px 9px;
  border-radius: var(--radius-lg, 14px);
  background: linear-gradient(180deg, #101828, #1d2939);
  box-shadow: var(--shadow-md);
  cursor: default;
  transition: transform 0.25s var(--ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1)), box-shadow 0.25s;
}

.dock-domain:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.dock-char {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.02em;
  background: linear-gradient(180deg, #ff5a64, #ffffff 45%, #ff5a64);
  background-size: 100% 260%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation:
    char-in 0.5s var(--ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1)) both,
    char-flow 3.2s linear infinite;
}

.dock-char.dot {
  color: var(--u2-primary, #E60012);
  background: none;
  -webkit-background-clip: initial;
  background-clip: initial;
}

@keyframes char-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes char-flow {
  from { background-position: 0 0; }
  to { background-position: 0 260%; }
}

.dock-domain:hover .dock-char {
  animation:
    char-in 0.5s var(--ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1)) both,
    char-flow 1.1s linear infinite;
}

.dock-dot-live {
  width: 5px;
  height: 5px;
  margin-top: 5px;
  border-radius: 50%;
  background: #22c55e;
  animation: live-blink 2s ease-in-out infinite;
}

@keyframes live-blink {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); }
  50% { opacity: 0.5; box-shadow: 0 0 0 4px rgba(34, 197, 94, 0); }
}

/* ---- 悬浮按钮 ---- */
.dock-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 46px;
  min-height: 46px;
  padding: 6px;
  border-radius: var(--radius-md, 10px);
  border: 1px solid var(--u2-stroke, #E5E7EB);
  background: #fff;
  color: var(--u2-muted, #667085);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s var(--ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1));
}

.dock-btn svg {
  width: 19px;
  height: 19px;
}

.dock-btn:hover,
.dock-btn.active {
  color: var(--u2-primary, #E60012);
  border-color: rgba(var(--u2-primary-rgb), 0.35);
  background: rgba(var(--u2-primary-rgb), 0.04);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.dock-btn.top {
  animation: top-in 0.3s var(--ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1)) both;
}

@keyframes top-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ---- 联系方式面板 ---- */
.dock-contact {
  position: relative;
}

.contact-panel {
  position: absolute;
  right: 58px;
  top: 50%;
  transform: translateY(-50%);
  width: 236px;
  padding: 12px;
  border-radius: var(--radius-lg, 14px);
  background: #fff;
  border: 1px solid var(--u2-stroke, #E5E7EB);
  box-shadow: var(--shadow-lg);
}

.contact-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 2px 4px 8px;
}

.contact-head strong {
  font-size: 14px;
  color: var(--u2-text, #101828);
}

.contact-head span {
  font-size: 11px;
  color: var(--u2-success, #0E9F57);
  font-weight: 600;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: var(--radius-md, 10px);
  text-decoration: none;
  transition: background 0.15s;
}

.contact-item:hover {
  background: rgba(var(--u2-primary-rgb), 0.05);
}

.contact-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md, 10px);
  background: rgba(var(--u2-primary-rgb), 0.06);
  border: 1px solid rgba(var(--u2-primary-rgb), 0.14);
  color: var(--u2-primary, #E60012);
  flex-shrink: 0;
}

.contact-icon svg {
  width: 18px;
  height: 18px;
}

.contact-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.contact-info strong {
  font-size: 13px;
  color: var(--u2-text, #101828);
}

.contact-info em {
  font-style: normal;
  font-size: 11px;
  color: var(--u2-muted, #667085);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-item > svg {
  width: 14px;
  height: 14px;
  color: var(--u2-muted, #667085);
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.15s;
}

.contact-item:hover > svg {
  opacity: 1;
  transform: translateX(0);
  color: var(--u2-primary, #E60012);
}

/* ---- 面板出入场 ---- */
.pop-enter-active {
  transition: all 0.22s var(--ease-out-expo, cubic-bezier(0.16, 1, 0.3, 1));
}

.pop-leave-active {
  transition: all 0.14s ease-in;
}

.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: translateY(-50%) translateX(8px) scale(0.94);
}

@media (max-width: 768px) {
  .side-dock {
    right: 10px;
    bottom: 90px;
  }
  .dock-domain {
    display: none;
  }
  .dock-btn {
    width: 42px;
    min-height: 42px;
  }
  .contact-panel {
    width: 210px;
    right: 52px;
  }
}
</style>
